import {
    BASE_VALUE,
    BulkSelectionContextValue,
} from '@/components/bulkQueue/tables/BulkSelectionContext';
import {
    Entity,
    useBulkSelectionStore,
    useBulkState,
    useSelectedEntityIdsKey,
} from '@/components/bulkQueue/tables/bulkContextUtils';
import { BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateAssignmentTable';
import {
    UseAssignmentDateRecsResult,
    useAssignmentDateRecs,
} from '@/components/decisionSupport/hooks/useAssignmentDateRecs';
import {
    canSelectVisit,
    pickTopRanked,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { VisitOverrideState } from '@/components/decisionSupport/utils/visitAssignmentInput.utils';
import { BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION } from '@/components/visitRequests/mutations/updateTentativeDate';
import {
    BulkUpdateVisitTentativeDateMutation,
    BulkUpdateVisitTentativeDateMutationVariables,
} from '@/components/visitRequests/mutations/updateTentativeDate.gql.types';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { errorToast } from '@/toast';
import {
    ProviderWritableStore,
    createDefaultWritableProviderStore,
    createStore,
    useStoreValue,
} from '@/utils/store';
import { useMutation } from '@apollo/client/react';
import { UpdateVisitTentativeDateInput } from 'gql-types';
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

type AssignmentDateResult = UseAssignmentDateRecsResult['resultsByVisitId'] extends ReadonlyMap<
    string,
    infer V
>
    ? V
    : never;
export type AssignmentDateRecommendation = AssignmentDateResult['dateRecommendation'];
type AssignmentDateDefinitions = UseAssignmentDateRecsResult['definitions'];

type BulkTentativeDateContextValue = BulkSelectionContextValue<{
    selectedRecommendationStore: ProviderWritableStore<string | null>;
    // DS visitAssignment flow. The cells read date recommendations from these; the
    // selected date string + submit flow through the shared stores.
    assignmentDefinitions: AssignmentDateDefinitions;
    assignmentResultsByVisitId: UseAssignmentDateRecsResult['resultsByVisitId'];
    assignmentSelectableIds: string[];
    // True when the sibling bulk-cancel selection column has a selection. While
    // disabled, every date checkbox + the select-all header checkbox are locked so
    // the two columns stay mutually exclusive.
    disabled: boolean;
}>;

const DEFAULT_VALUE: BulkTentativeDateContextValue = {
    ...BASE_VALUE,
    selectedRecommendationStore: createDefaultWritableProviderStore<string | null>(null),
    assignmentDefinitions: null,
    assignmentResultsByVisitId: new Map(),
    assignmentSelectableIds: [],
    disabled: false,
};

export const useUpdateVisitTentativeDates = () => {
    return useMutation<
        BulkUpdateVisitTentativeDateMutation,
        BulkUpdateVisitTentativeDateMutationVariables
    >(BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION, {
        onError: (error) => {
            errorToast(error.message);
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
        awaitRefetchQueries: true,
    });
};

const BulkTentativeDateContext = createContext<BulkTentativeDateContextValue>(DEFAULT_VALUE);

type BulkTentativeDateContextProviderProps = {
    children: ReactNode;
    entities: Entity[];
    /** Lock all date selection when the sibling bulk-cancel column has a selection. */
    disabled?: boolean;
};

export const BulkTentativeDateContextProvider = (props: BulkTentativeDateContextProviderProps) => {
    const { children, entities, disabled = false } = props;
    const [isEntitySelectedStore, setValue, selectedCount, resetSelection, pruneToEntityIds] =
        useBulkSelectionStore();
    const selectedRecommendationStoreRef = useRef(createStore<string | null>());
    const selectedRecommendationStore = selectedRecommendationStoreRef.current;

    // Prune selections when the displayed entities change (e.g. filter change).
    useEffect(() => {
        const entityIds = new Set(entities.map((e) => e.id));
        pruneToEntityIds(entityIds);
    }, [entities, pruneToEntityIds]);

    // DS visitAssignment flow (visits only). Date recommendations hold the
    // visit's currently-assigned clinician fixed automatically (implicit keep —
    // we send no clinician override) and choose the date.
    const visitOverrideStates = useMemo<VisitOverrideState[]>(
        () =>
            entities
                .filter((entity) => entity.__typename === 'Visit')
                .map((entity) => ({
                    visitId: entity.id,
                    appointments: [],
                    dateOverride: undefined,
                })),
        [entities],
    );
    const {
        definitions: assignmentDefinitions,
        resultsByVisitId: assignmentResultsByVisitId,
        loading: assignmentLoading,
    } = useAssignmentDateRecs(visitOverrideStates);

    // Reset picks when the recs change (a new DS set); identical recs keep the same ref.
    useEffect(() => {
        selectedRecommendationStore.setAll(new Map());
    }, [assignmentResultsByVisitId, selectedRecommendationStore]);

    const getDefaultDate = useCallback(
        (entityId: string): string | null => {
            const recs =
                assignmentResultsByVisitId.get(entityId)?.dateRecommendation.recommendations;
            const best = pickTopRanked(recs);
            return best ? GraphInputHelpers.date(best.date) : null;
        },
        [assignmentResultsByVisitId],
    );

    // Recomputes the selectable set (Select All + header count) when the user picks;
    // the selection store mutates outside React (see useSelectedEntityIdsKey).
    const pickedVisitIdsKey = useSelectedEntityIdsKey(
        selectedRecommendationStore,
        (date) => date != null,
    );

    // A visit is selectable when it has a recommended default OR an explicit pick
    const assignmentSelectableIds = useMemo<string[]>(() => {
        const pickedVisitIds = new Set(pickedVisitIdsKey ? pickedVisitIdsKey.split(',') : []);
        return entities
            .filter((entity) => entity.__typename === 'Visit')
            .filter((entity) =>
                canSelectVisit(
                    (assignmentResultsByVisitId.get(entity.id)?.dateRecommendation.recommendations
                        .length ?? 0) > 0,
                    pickedVisitIds.has(entity.id),
                ),
            )
            .map((entity) => entity.id);
    }, [entities, assignmentResultsByVisitId, pickedVisitIdsKey]);

    const [mutateVisits, { loading: submitting }] = useUpdateVisitTentativeDates();

    const submit = useCallback(async () => {
        const selected = isEntitySelectedStore.getAll();
        const visits: UpdateVisitTentativeDateInput[] = [];

        selected.forEach((isSelected: boolean, entityId: string) => {
            if (!isSelected) {
                return;
            }
            const date =
                selectedRecommendationStore.getSnapshot(entityId) ?? getDefaultDate(entityId);
            if (!date) {
                return;
            }
            visits.push({ visitId: entityId, tentativeDate: date });
        });

        if (visits.length > 0) {
            await mutateVisits({ variables: { input: { visits } } });
        }
        resetSelection();
    }, [
        mutateVisits,
        isEntitySelectedStore,
        selectedRecommendationStore,
        getDefaultDate,
        resetSelection,
    ]);

    const state = useBulkState(assignmentLoading, submitting);

    const value: BulkTentativeDateContextValue = {
        selectedRecommendationStore: {
            ...selectedRecommendationStore,
            setValue: selectedRecommendationStore.setValue,
        },
        isEntitySelectedStore: {
            ...isEntitySelectedStore,
            setValue,
        },
        submit,
        state,
        selectedCount,
        entityCount: entities.length,
        assignmentDefinitions,
        assignmentResultsByVisitId,
        assignmentSelectableIds,
        disabled,
    };

    return (
        <BulkTentativeDateContext.Provider value={value}>
            {children}
        </BulkTentativeDateContext.Provider>
    );
};

export const useBulkTentativeDateContext = () => {
    const {
        submit,
        state,
        selectedCount,
        entityCount,
        isEntitySelectedStore,
        assignmentSelectableIds,
        disabled,
    } = useContext(BulkTentativeDateContext);
    const summary = useTentativeDateSummary();

    const selectableCount = assignmentSelectableIds.length;

    const selectAll = useCallback(() => {
        const currentlySelected = isEntitySelectedStore.getAll();
        const allSelected =
            assignmentSelectableIds.length > 0 &&
            assignmentSelectableIds.every((id) => currentlySelected.get(id) === true);
        assignmentSelectableIds.forEach((id) => {
            isEntitySelectedStore.setValue?.(id, !allSelected);
        });
    }, [assignmentSelectableIds, isEntitySelectedStore]);

    return useMemo(
        () => ({
            submit,
            state,
            selectedCount,
            entityCount,
            summary,
            selectableCount,
            selectAll,
            disabled,
        }),
        [selectedCount, summary, entityCount, state, submit, selectableCount, selectAll, disabled],
    );
};

export const useTentativeDateSummary = () => {
    const { selectedRecommendationStore, isEntitySelectedStore } =
        useContext(BulkTentativeDateContext);

    return useSyncExternalStore(
        (cb: () => void) => {
            const unsub1 = selectedRecommendationStore.subscribeToAll(cb);
            const unsub2 = isEntitySelectedStore.subscribeToAll(cb);
            return () => {
                unsub1();
                unsub2();
            };
        },
        () => {
            const dates = new Set<string>();
            const selected = isEntitySelectedStore.getAll();
            selected.forEach((isSelected: boolean, entityId: string) => {
                if (!isSelected) {
                    return;
                }
                const date = selectedRecommendationStore.getSnapshot(entityId);
                if (date) {
                    dates.add(date);
                }
            });
            const numSelected = Array.from(selected.values()).filter(Boolean).length;
            const dateList = Array.from(dates).join(', ');
            return `Assign date${dates.size > 1 ? 's' : ''} to ${numSelected} visits: ${dateList}`;
        },
    );
};

// Per-visit date recommendation + shared definitions for the new flow.
export const useAssignmentDateRec = (
    entityId: string,
): {
    definitions: AssignmentDateDefinitions;
    dateRecommendation: AssignmentDateRecommendation | null;
} => {
    const { assignmentDefinitions, assignmentResultsByVisitId } =
        useContext(BulkTentativeDateContext);
    return {
        definitions: assignmentDefinitions,
        dateRecommendation: assignmentResultsByVisitId.get(entityId)?.dateRecommendation ?? null,
    };
};

export const useIsEntitySelected = (entityId: string): [boolean, (isSelected: boolean) => void] => {
    const { isEntitySelectedStore } = useContext(BulkTentativeDateContext);
    const isSelected = useStoreValue(BulkTentativeDateContext, 'isEntitySelectedStore', entityId);

    const setIsSelected = useCallback(
        (selected: boolean) => {
            isEntitySelectedStore.setValue?.(entityId, selected);
        },
        [isEntitySelectedStore, entityId],
    );

    return [isSelected, setIsSelected];
};

export const useSelectedDate = (
    entityId: string,
): [string | null, (date: string | null) => void] => {
    const { selectedRecommendationStore, isEntitySelectedStore } =
        useContext(BulkTentativeDateContext);
    const selectedDate = useStoreValue(
        BulkTentativeDateContext,
        'selectedRecommendationStore',
        entityId,
    );

    const setSelectedDate = useCallback(
        (date: string | null) => {
            selectedRecommendationStore.setValue(entityId, date);
            // Auto-select the entity when a date is chosen
            if (date) {
                isEntitySelectedStore.setValue?.(entityId, true);
            }
        },
        [selectedRecommendationStore, isEntitySelectedStore, entityId],
    );

    return [selectedDate ?? null, setSelectedDate];
};
